import jsonwebtoken from 'jsonwebtoken';
import { observable, action } from 'mobx';
import { deduplicateRequest } from 'utils';
import { analytics } from 'core/analytics';
// jwt refreshing setInterval variable
let jwtRefreshing = null;

// const for the local storage key name
const jwtStorageKey = 'jwt';
const jwtDataStorageKey = 'jwtData';
const refreshJwtStorageKey = 'refreshJwt';
const refreshJwtDataStorageKey = 'refreshJwtData';

export const tokenType = {
  refreshToken: 'refreshToken',
  accessToken: 'accessToken',
};

export const AUTH_ERROR_ALERTS = {
  sessionExpired: {
    level: 'info',
    message: 'Your session has expired. Please sign in again.',
  },
};

// observable for the status of the jwt refreshing
export const tokenData = observable({
  jwt: '',
  jwtData: {},
  refreshJwtData: {},
  refreshJwt: '',
  connectErrCount: 0,
});

export function SetAccessTokenData(jwt, jwtData) {
  // do further check for jwt format or length?
  localStorage.setItem(jwtStorageKey, jwt);
  localStorage.setItem(jwtDataStorageKey, JSON.stringify(jwtData));
  action(() => {
    tokenData.connectErrCount = 0;
    tokenData.jwt = jwt;
    tokenData.jwtData = jwtData;
  })();
}

export function SetRefreshTokenData(jwt, jwtData) {
  localStorage.setItem(refreshJwtStorageKey, jwt);
  localStorage.setItem(refreshJwtDataStorageKey, JSON.stringify(jwtData));
  action(() => {
    tokenData.connectErrCount = 0;
    tokenData.refreshJwt = jwt;
    tokenData.refreshJwtData = jwtData;
  })();
}

// method to remove jwt from local storage
export const RemoveJwt = () => {
  // remove the jwt
  action(() => {
    tokenData.jwt = '';
    tokenData.jwtData = {};
    tokenData.refreshJwt = '';
    tokenData.refreshJwtData = {};
  })();
  localStorage.removeItem(jwtStorageKey);
  localStorage.removeItem(jwtDataStorageKey);
  localStorage.removeItem(refreshJwtStorageKey);
  localStorage.removeItem(refreshJwtDataStorageKey);
};

export function SetToken(jwt, type = tokenType.accessToken) {
  try {
    const decoded = jsonwebtoken.decode(jwt);
    if (decoded) {
      if (type === tokenType.accessToken) {
        SetAccessTokenData(jwt, decoded);
      } else if (type === tokenType.refreshToken) {
        SetRefreshTokenData(jwt, decoded);
      }
    } else {
      RemoveJwt();
    }
  } catch (err) {
    RemoveJwt();
    console.error(`${err.name}: ${err.message}`);
  }
}

const IncrConnErr = () => {
  action(() => {
    tokenData.connectErrCount += 1;
  })();
};

// function to calculate the delay time;
const reconnectDelayTime = () => {
  const delayTime = tokenData.connectErrCount * 500;

  // maximum delay time is 3 seconds
  return delayTime < 3000 ? delayTime : 3000;
};

// function to get the most up-to-date jwt
export const getJwt = () => {
  // if jwt is expired we should removeJwt and re-request it
  return tokenData.jwt;
};

export const getRefreshJwt = () => {
  return tokenData.refreshJwt;
};

// function to get the most up-to-date jwtData
const getJwtData = () => {
  // if jwt is expired we should removeJwt and re-request it
  return tokenData.jwtData;
};

let lastTokenFetchTime = null;

// For the param userId, it can be only used for admin impersonation
// When your current role is admin, and you want to impersonate a user
// You need to pass the userId to get the token
export const refreshAccessToken = async (clientStore, userId) => {
  // use refresh token to get the new access jwt
  const response = await fetch('/auth/oauth/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      refresh_token: getRefreshJwt(),
      grant_type: 'refresh_token',
      user_id: userId,
    }),
  });

  // user is not logged in, can not get the token
  if (response.status === 401) {
    const noRedirectionRoutes = [
      'home.signIn',
      'home.signUp',
      'onboarding.checkEmail',
    ];

    if (clientStore) {
      if (
        !noRedirectionRoutes.some(
          route => clientStore.routerStore.route.name === route,
        )
      ) {
        clientStore.SignOutUser(
          AUTH_ERROR_ALERTS.sessionExpired,
          window.location.pathname,
        );
      }
    }
    return null;
  }

  const jwt = await response.json();

  try {
    const decoded = jsonwebtoken.decode(jwt);
    if (decoded) {
      SetAccessTokenData(jwt, decoded);
    } else {
      RemoveJwt();
    }
  } catch (err) {
    IncrConnErr();
    RemoveJwt();
    console.error(`${err.name}: ${err.message}`);
  }

  return jwt;
};

const fetchToken = async clientStore => {
  if (lastTokenFetchTime && Date.now() - lastTokenFetchTime < 5000) {
    // "Debounce": if we succesfully got a token a moment ago, just return the current data
    // this error probably comes from a request using the previous token
    return getJwt();
  }

  // delay refresh token based on the error count
  await new Promise(resolve => {
    setTimeout(resolve, reconnectDelayTime());
  });

  const jwt = await refreshAccessToken(clientStore);

  if (clientStore.isImpersonating()) {
    clientStore.clearImpersonationSession();
    clientStore.clearCountdownSeconds();

    window.location.replace('/');
  }

  lastTokenFetchTime = Date.now();
  return jwt;
};

export const refresher = {
  clientStore: null,
  refreshToken: deduplicateRequest(() => fetchToken(refresher.clientStore)),
};

function SetTokenDataFromLocal() {
  const jwtData = localStorage.getItem(jwtDataStorageKey);
  const refreshJwtData = localStorage.getItem(refreshJwtDataStorageKey);
  const jwtDataObj = jwtData ? JSON.parse(jwtData) : {};
  const refreshJwtDataObj = refreshJwtData ? JSON.parse(refreshJwtData) : {};
  const jwt = localStorage.getItem(jwtStorageKey);
  const refreshJwt = localStorage.getItem(refreshJwtStorageKey);
  if (jwt !== null) {
    // the localStorage key exists
    SetAccessTokenData(jwt, jwtDataObj);
  }
  if (refreshJwt !== null) {
    SetRefreshTokenData(refreshJwt, refreshJwtDataObj);
  }
  if (jwt === 'null') {
    RemoveJwt();
  }
}

// Check if the jwt is going to expire in the next 4 minutes.
// if it is going to expire refresh the token.
export const startJwtRefreshing = async () => {
  SetTokenDataFromLocal();
  // check if the jwt is set
  const jwt = getJwt();
  if (!jwt) {
    // call refresh token
    await refresher.refreshToken();
  }
  // check if the interval is running
  if (!jwtRefreshing) {
    jwtRefreshing = setInterval(async () => {
      const decoded = getJwtData();
      const now = new Date().getTime() / 1000;
      const exp = decoded?.exp || -1;
      if (now + 4 * 60 > exp) {
        await refresher.refreshToken();
      }
    }, 4 * 60 * 1000);
  }
};

// function to stop the jwt refreshing interval
export const stopJwtRefreshing = () => {
  if (jwtRefreshing) {
    clearInterval(jwtRefreshing);
    jwtRefreshing = null;
  }
};
